import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaCodeBranch, FaFileCode, FaBook, FaArchive, FaSlidersH } from "react-icons/fa";
import { CHANGES_CONSTANTS } from "../_MODULES/_API";
import { ChangeDiffView } from "./ChangeDiffView";
import { OPTIONS_CHANGE_ACTION, onFindOptionLabel } from "../../../_Catalogs.Views";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { SearchFilter } from "../../../../_UI/SearchFilter";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";

export const PanelChanges = ({ subProjectId, projectId }) => {

    const [useSearch, setSearch] = useState("");
    const [useAction, setAction] = useState("");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);

    const { apiGet: onGetChanges, useApiData: CHANGES, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate } = useApiActionMutation({ isToastError: true, isToastSuccess: true, minTimeMutate: 300 });

    const onLoadChanges = () => onGetChanges({
        endpoint: CHANGES_CONSTANTS.LIST,
        config: {
            params: {
                subProjectId,
                search: useSearch || undefined,
                action: useAction || undefined,
            },
        },
    });

    useEffect(() => {
        const TIMER = setTimeout(onLoadChanges, 250);
        return () => clearTimeout(TIMER);
    }, [subProjectId, useSearch, useAction]); // eslint-disable-line react-hooks/exhaustive-deps

    const ITEMS = CHANGES ?? [];

    const GROUPS = useMemo(() => {

        const BY_FILE = new Map();

        ITEMS.forEach((change) => {

            if (!BY_FILE.has(change.filePath)) {
                BY_FILE.set(change.filePath, { filePath: change.filePath, changes: [] });
            }

            BY_FILE.get(change.filePath).changes.push(change);
        });

        return [...BY_FILE.values()];

    }, [ITEMS]);

    const onDelete = async (change) => {

        const IS_CONFIRMED = await confirm.danger("Archivar cambio", `Se archiva el cambio sobre "${change.filePath}".`);

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: CHANGES_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: change.id }, successMessage: "Cambio archivado",
        });

        if (RESPONSE.success) mutateLocal({ id: change.id, remove: true });
    };

    const PAGER = usePagedList(GROUPS, 6, "changesPage");

    const ACTION_OPTIONS = [{ value: "", label: "Todas las acciones" }, ...OPTIONS_CHANGE_ACTION];

    return (
        <>
            <div className="LStudio__app_panel_bar">

                <ViewToolbar
                    filters={<>
                        <SearchFilter value={useSearch} onChange={setSearch} placeholder="Ej.: auth.js, permisos o commit..." />
                        <AppButton variant={useAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />}
                            onClick={() => setAdvancedOpen((current) => !current)}>
                            {useAction ? "Avanzado (1)" : "Avanzado"}
                        </AppButton>
                    </>}
                    expandedContent={<AdvancedFilterPanel isOpen={useAdvancedOpen} activeCount={useAction ? 1 : 0}
                        onClear={() => setAction("")}>
                        <FieldSelect id="change_action" label="Acción" options={ACTION_OPTIONS} isSearchable
                            value={useAction} onChange={(event) => setAction(event.target.value)} />
                    </AdvancedFilterPanel>}
                    total={ITEMS.length} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={[
                        { key: "filePath", label: "Fichero" },
                        { key: "action", label: "Acción" },
                        { key: "language", label: "Lenguaje" },
                        { key: "description", label: "Motivo" },
                        { key: "logbook", label: "Entrada" },
                    ]}
                    exportRows={ITEMS.map((change) => ({
                        filePath: change.filePath,
                        action: onFindOptionLabel(OPTIONS_CHANGE_ACTION, change.action),
                        language: change.language,
                        description: change.description ?? "",
                        logbook: change.logbook?.title ?? "",
                    }))}
                    exportName="cambios" />

            </div>

            <p className="LStudio__app_todos_hint">
                Los cambios se cargan dentro de una entrada de bitácora y acá los ves agrupados por fichero,
                para seguir la historia de un archivo sin buscar entrada por entrada.
            </p>

            {IS_LOADING && <Skeleton variant="box" height={220} count={2} />}

            {
                !IS_LOADING && ITEMS.length === 0 && (
                    <EmptyState icon={<FaCodeBranch />} title="Sin cambios registrados"
                        description="Cargá el fichero, el código anterior y el nuevo desde una entrada de bitácora." />
                )
            }

            {
                !IS_LOADING && PAGER.paged.map((group) => (
                    <Card key={group.filePath} className="LStudio__app_changes_group">

                        <div className="LStudio__app_changes_group_head">

                            <FaFileCode aria-hidden="true" />

                            <code className="LStudio__app_changes_group_file">{group.filePath}</code>

                            <Badge tone="neutral" size="sm">
                                {group.changes.length} {group.changes.length === 1 ? "cambio" : "cambios"}
                            </Badge>

                        </div>

                        <div className="LStudio__app_changes_group_list">
                            {
                                group.changes.map((change) => (
                                    <div key={change.id} className="LStudio__app_changes_group_item">

                                        <div className="LStudio__app_changes_group_origin">

                                            <FaBook aria-hidden="true" />

                                            <Link to={`/projects/${projectId}/subprojects/${subProjectId}/logbook`}
                                                className="LStudio__app_changes_group_origin_link">
                                                {change.logbook?.title ?? "Entrada de bitácora"}
                                            </Link>

                                        </div>

                                        <ChangeDiffView change={change} hasFilePath={false}
                                            actions={
                                                <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />}
                                                    isIconOnly aria-label="Archivar cambio"
                                                    onClick={() => onDelete(change)} />
                                            } />

                                    </div>
                                ))
                            }
                        </div>

                    </Card>
                ))
            }
            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />
        </>
    );
};
